<script>
  import { router, page, useForm } from "@inertiajs/svelte"
  import route from "@helpers/route"
  import { Page } from "@objects/Page"
  import asset from "@helpers/asset"
  import ContentContainer from "@objects/ContentContainer.svelte"
  import Wrap from "@objects/Wrap.svelte"
  import SubmitButton from "@components/SubmitButton.svelte"
  import Alert from "@components/Alert.svelte"
  import CartItemEditorCard from "./CartItemEditorCard.svelte"
  import Link from "@components/Link.svelte"
  import { onMount } from "svelte"
  import Analytics from "@services/Analytics"
  import CouponForm from "@components/CouponForm.svelte"
  import PaymentSummary from "@components/PaymentSummary.svelte"
  import { mapToAnalyticsData } from "@helpers/models/lineItem"
  import plural from "@helpers/plural"
  import { merchant } from "@store"

  export let canCheckout
  export let items
  export let payment
  export let cartExists
  export let cart

  let account = $page.props.account

  const form = useForm()

  let groupedByCollections

  $: groupedByCollections = Object.values(
    items.reduce((acc, item) => {
      const collId = item.collection.id

      if (!acc[collId]) {
        acc[collId] = []
      }

      acc[collId].push(item)

      return acc
    }, {}),
  )

  $: itemsAwaitingStockCount = items.filter((lineItem) => {
    // Items that have inventory in stock are excluded from this count
    return lineItem.product.inventory_remaining < 1
  }).length

  function submit() {
    $form.post(route("shop.checkout.cart.store"), {
      onSuccess: () => {
        Analytics.track("User started checkout", {
          orderId: cart.id,
          value: payment.items,
          revenue: payment.items,
          items: mapToAnalyticsData(items),
        })
      },
    })
  }

  function handleUpdateCartItem() {
    router.reload({
      preserveScroll: true,
    })
  }

  onMount(() => {
    Analytics.track("User viewed cart", {
      collectionId: items[0]?.collection.id,
      items: mapToAnalyticsData(items),
    })
  })
</script>

<svelte:head>
  <title>Your cart</title>
</svelte:head>

<Page>
  <Wrap>
    <ContentContainer>
      <h1 class="[ text-5xl md:text-6xl ]">My Shopping Cart</h1>

      <div class="grid gap-20 lg:grid-cols-3 mt-8">
        <div class="[ col-span-1 lg:col-span-2 ]">
          {#if $page.props.errors.cart}
            <Alert type="error" class="[ mb-12 ]">
              {$page.props.errors.cart}
            </Alert>
          {/if}

          {#if $page.props.errors.taxes}
            <Alert type="error" class="[ mb-12 ]">
              {$page.props.errors.taxes}
            </Alert>
          {/if}

          <section>
            {#if cartExists && items.length > 0}
              {#each groupedByCollections as items}
                {@const collection = items[0].collection}
                <div class="[ flex flex-col mb-6 ]">
                  <img
                    src={asset(collection.cover_photo_url, { width: 1650 })}
                    alt=""
                    class="[ w-full h-auto mb-2 ]"
                    class:hidden={!collection.cover_photo_url}
                  />

                  <Link
                    type="stroke"
                    href={route("shop.collections.show", [collection, collection.slug])}
                    class="[ text-4xl font-heading ]"
                  >
                    {collection.name}
                  </Link>
                  <span>{items.length} products</span>
                </div>

                <div class="[ grid gap-x-6 gap-y-10 md:grid-cols-3 mb-24 ]">
                  {#each items as item (item.id)}
                    <CartItemEditorCard {item} {cart} on:update={handleUpdateCartItem} />
                  {/each}
                </div>
              {/each}
            {:else}
              <p>Your cart is empty.</p>
            {/if}
          </section>
        </div>

        <aside class="[ cols-span-1 self-start space-y-8 ]">
          <PaymentSummary
            {cart}
            siteCreditBalanceUse={cart.site_credit_balance_use}
            {payment}
            {account}
            showSubtotal={true}
          >
            <form on:submit|preventDefault={submit}>
              {#if itemsAwaitingStockCount > 0}
                <Alert type="warning" class="col-span-5 lg:col-span-3 mb-4">
                  <p class="text-left">
                    {itemsAwaitingStockCount}
                    {plural("product", itemsAwaitingStockCount)} in this cart {itemsAwaitingStockCount > 1
                      ? "are"
                      : "is"}
                    waiting for new stock or made to order. Vendor-provided shipping lead time estimates are shown when available.
                    Dates subject to change, and not guaranteed.
                  </p>
                </Alert>
              {/if}

              <SubmitButton size="large" disabled={!canCheckout} processing={$form.processing}>
                Start Checkout &rarr;
              </SubmitButton>
            </form>
          </PaymentSummary>

          {#if !$merchant.is_firms_plus}
            <CouponForm {account} {cart} />
          {/if}
        </aside>
      </div>
    </ContentContainer>
  </Wrap>
</Page>
